// Lightweight i18n: a flat key→string dictionary per language, a zustand store for
// the current language (persisted), and a useT() hook returning a bound t().
import { create } from 'zustand';

export type Lang = 'en' | 'uk';

type Dict = Record<string, string>;

const en: Dict = {
  'nav.leaderboard': 'Leaderboard',
  'nav.admin': 'Admin',

  'join.title': 'Join the quiz',
  'join.subtitle': 'Enter the game code from the host and pick a nickname.',
  'join.code': 'Game code',
  'join.codePlaceholder': 'e.g. K7QP',
  'join.nickname': 'Nickname',
  'join.namePlaceholder': 'Your name',
  'join.submit': 'Join game',
  'join.pickNickname': 'Game {code} — pick a nickname.',

  'play.connected': 'connected',
  'play.reconnecting': 'reconnecting…',
  'play.joining': 'Joining game {code}…',
  'play.youreIn': "You're in! 🎉",
  'play.waitingHost': 'Waiting for the host to start the quiz.',
  'play.pts': '{n} pts',
  'play.iKnow': 'I KNOW',
  'play.youBuzzed': 'You buzzed!',
  'play.shout': 'SHOUT!',
  'play.youShouted': 'You shouted!',
  'play.buzzersClosed': 'Buzzers closed — the host is picking.',
  'play.getReady': 'Get ready…',
  'play.waitingNext': 'Waiting for the next question…',
  'play.calledOn': '📣 Host called on {name}',
  'play.correct': '✅ {name} got it right (+{points})',
  'play.wrong': '❌ {name} was wrong',
  'play.buzzedIn': 'Buzzed in:',
  'play.scores': 'Scores',
  'play.gameOver': '🏁 Game over!',
  'play.winner': '🥇 {name} wins with {score} pts',
  'play.leave': 'Leave game',
  'play.donate': 'Донат',
  // «Норм чи Стрьом» (vote rounds)
  'play.voteNorm': 'NORM',
  'play.voteCringe': 'CRINGE',
  'play.voteNow': 'Vote — you can change your mind until everyone has voted or the timer ends.',
  'play.voteCast': 'Vote counted. Tap the other button to change it.',
  'play.voteClosed': 'Voting closed.',
  'play.voteResults': 'The room says…',
  'play.nobodyVoted': 'Nobody voted.',
  'play.voteSummary': 'Every statement, as the room saw it',
  'kind.label': 'Game kind',
  'kind.quiz': '🔔 Quiz',
  'kind.normcringe': '👍👎 Norm or Cringe',
  'templates.voteLabels': 'Button wording (optional)',
  'templates.labelNorm': 'Left button (the "norm" side)',
  'templates.labelCringe': 'Right button (the "cringe" side)',
  'templates.voteLabelsHelp': 'Leave blank for NORM / CRINGE. Fill in both to rename, e.g. Yes / No.',
  'templates.labelsBoth': 'Fill in both button labels, or leave both blank.',
  'templates.window': 'Buzzer / vote window (seconds)',
  'templates.windowHelp': 'Blank = 30s. How long players have after you open a question.',
  'templates.windowRange': 'Use a whole number between 5 and 120.',
  'console.fastest': 'Scored (faster = more, up to {max})',
  'console.hideAnswers': '🙈 Hide answers',
  'console.showAnswers': '👁 Show answers',
  'console.hideAnswersHelp': 'Hide the answer note and the correct side until the round closes — for when this screen is projected.',
  'windowSeconds must be between 5 and 120': 'window must be between 5 and 120 seconds',

  'login.title': 'Host sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.newPassword': 'New password',
  'login.firstSignin': 'First sign-in — choose a new password.',
  'login.signin': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.setPassword': 'Set password & continue',
  'login.failed': 'Sign-in failed',

  'games.title': 'Host dashboard',
  'games.templates': 'Templates',
  'games.authoring': 'Authoring',
  'games.history': 'History',
  'games.signout': 'Sign out',
  'games.newGame': 'New game',
  'games.pickTopics': 'Tap topics in the order you want to play them.',
  'games.noTopics': 'No topics yet.',
  'games.createTopics': 'Create topics',
  'games.filterTopics': 'Filter topics…',
  'games.noMatch': 'No topics match your filter.',
  'games.createGame': 'Create game ({n})',
  'games.creating': 'Creating…',
  'games.donateLabel': 'Donate link (optional)',
  'games.donatePlaceholder': 'https://…',
  'games.donateHelp': 'Shown to players on the join and game screens.',
  'games.recentGames': 'Recent games',
  'games.noGames': 'No games yet.',
  'games.openConsole': 'Open console',
  'games.viewResults': 'View results',
  'games.deleteConfirm': 'Delete game {code}? This removes its players, scores and results permanently.',

  'templates.title': 'Game templates',
  'templates.intro': 'Reusable topic lineups. Set one up once, then start games from it.',
  'templates.new': 'New template',
  'templates.yourTemplates': 'Your templates',
  'templates.none': 'No templates yet. Create one to save a topic lineup.',
  'templates.topicCount': '{n} topics',
  'templates.startGame': 'Start game',
  'templates.deleteConfirm': 'Delete template “{name}”? Topics and games are not affected.',
  'templates.newTitle': 'New template',
  'templates.editTitle': 'Edit template',
  'templates.namePlaceholder': 'Template name',
  'templates.lineup': 'Topics (drag to reorder)',
  'templates.emptyLineup': 'No topics yet — add some below.',
  'templates.addTopics': 'Add topics',
  'templates.allAdded': 'All topics are already in this template.',
  'templates.nameRequired': 'Give the template a name first.',
  'templates.noTopicsLeft': "This template's topics no longer exist.",
  'templates.dragHandle': 'Drag to reorder',

  'status.lobby': 'lobby',
  'status.active': 'active',
  'status.finished': 'finished',
  'qstate.idle': 'idle',
  'qstate.open': 'open',
  'qstate.closed': 'closed',
  'res.correct': 'correct',
  'res.incorrect': 'incorrect',

  'console.backGames': 'Games',
  'console.live': 'live',
  'console.connecting': 'connecting…',
  'console.loading': 'Loading console…',
  'console.join': 'Join:',
  'console.copyLink': 'Copy link',
  'console.copied': '✓ Copied',
  'console.showQR': 'Show QR',
  'console.hideQR': 'Hide QR',
  'console.waitingPlayers': 'Waiting for players…',
  'console.pressStart': 'Press Start game when everyone’s in.',
  'console.startGame': 'Start game',
  'console.answer': 'Answer: {answer}',
  'console.noQuestion': 'No question selected.',
  'console.openBuzzer': 'Open buzzer ({s}s)',
  'console.resetBuzzer': '🔄 Reset buzzer',
  'console.endGame': 'End game',
  'console.endGameConfirm': 'End the game?',
  'console.gameFinished': 'Game finished.',
  'console.buzzedIn': 'Buzzed in ({n})',
  'console.noBuzzes': 'No buzzes yet.',
  'console.callOn': 'Call on',
  'console.correct': 'correct (+{points})',
  'console.wrong': 'wrong',
  'console.undo': 'Undo',
  'console.tooLate': 'Buzzed more than 5s after the correct answer — can no longer score',
  'console.mode': 'Question mode',
  'console.modeClassic': '🔔 Buzzer',
  'console.modeShout': '📣 Shouting match',
  'console.shoutTitle': 'Shouting match — award the answer',
  'console.shoutHint': 'Tap players to pick the winners (📣 = shouted). Points split equally, rounded up, never below the price ÷ 10.',
  'console.selectShouted': 'Select all who shouted ({n})',
  'console.award': 'Award {points} pts each ({n})',
  'console.scores': 'Scores',
  'console.noPlayers': 'No players yet.',
  'console.stateLabel': 'state: {status} / {qstate}',
  'console.openVote': 'Open vote ({s}s)',
  'console.resetVote': '🔄 Reset vote',
  'console.votes': 'Votes ({n})',
  'console.votesLive': '{n} of {total} players have voted. The split is revealed once everyone has voted or the timer ends.',
  'console.votesIdle': 'Open the vote when you have read the statement out.',
  'console.players': 'Players ({n})',

  'nav.prevTopic': 'Previous topic',
  'nav.prevQuestion': 'Previous question',
  'nav.nextQuestion': 'Next question',
  'nav.nextTopic': 'Next topic',

  'qr.scan': 'Scan to join instantly',

  'authoring.title': 'Authoring',
  'authoring.bulkImport': 'Bulk import',
  'authoring.importHelp': 'Upload a JSON file (see docs/import-template.json). Quiz topics need 5 questions; Norm-or-Cringe topics (kind: "normcringe", see docs/norm-cringe-template.json) take any number of statements.',
  'authoring.imported': 'Imported {imported}, skipped {skipped}.',
  'authoring.topics': 'Topics',
  'authoring.newTopicPlaceholder': 'New topic title',
  'authoring.add': '＋ Add',
  'authoring.filterTopics': 'Filter topics…',
  'authoring.newestFirst': 'Newest first',
  'authoring.titleAZ': 'Title A–Z',
  'authoring.noTopics': 'No topics yet — add one above or import a JSON file.',
  'authoring.noMatch': 'No topics match your filter.',
  'authoring.deleteTopicConfirm': 'Delete this topic and its questions?',
  'authoring.deleteQuestionConfirm': 'Delete this question?',
  'authoring.qCount': '{n} Q',
  'authoring.questionText': 'Question text',
  'authoring.answerHostOnly': 'Answer (host only)',
  'authoring.statementText': 'Statement to vote on',
  'authoring.hostNote': 'Host note (optional)',
  'authoring.attachMedia': '📎 Picture / audio',
  'authoring.replaceMedia': 'Replace',
  'authoring.uploading': 'Uploading…',
  'authoring.uploadFailed': 'Upload failed',
  'authoring.media.image': 'picture',
  'authoring.media.audio': 'audio (host only)',
  'play.listenHost': 'Listen on the host screen',
  'play.listenHostBoth': 'Both clips play on the host screen — then pick one',
  'authoring.sideMedia': 'Per-answer media',
  'play.correctAnswer': 'Correct answer: {answer}',
  'play.youWereRight': '✅ You got it — +{points}',
  'play.youWereWrong': '❌ Not this time',
  'authoring.correctSide': 'Correct side (optional)',
  'authoring.correctNone': 'Opinion only',
  'authoring.importFailed': 'Import failed',
  'authoring.failedLoad': 'Failed to load',

  'lb.title': '🏆 All-time leaderboard',
  'lb.loading': 'Loading…',
  'lb.noGames': 'No games have finished yet.',
  'lb.games': '{n} games',

  'history.title': 'Past games',
  'history.noFinished': 'No finished games yet.',
  'history.noPlayers': 'No players.',

  'common.back': '← Games',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.add': 'Add',
};

const uk: Dict = {
  'nav.leaderboard': 'Рейтинг',
  'nav.admin': 'Адмін',

  'join.title': 'Приєднатися до вікторини',
  'join.subtitle': 'Введіть код гри від ведучого та оберіть нікнейм.',
  'join.code': 'Код гри',
  'join.codePlaceholder': 'напр. K7QP',
  'join.nickname': 'Нікнейм',
  'join.namePlaceholder': "Ваше ім'я",
  'join.submit': 'Приєднатися',
  'join.pickNickname': 'Гра {code} — оберіть нікнейм.',

  'play.connected': 'підключено',
  'play.reconnecting': 'перепідключення…',
  'play.joining': 'Приєднання до гри {code}…',
  'play.youreIn': 'Ви в грі! 🎉',
  'play.waitingHost': 'Очікуємо, поки ведучий розпочне вікторину.',
  'play.pts': '{n} балів',
  'play.iKnow': 'Я ЗНАЮ',
  'play.youBuzzed': 'Ви натиснули!',
  'play.shout': 'КРИЧІТЬ!',
  'play.youShouted': 'Ви крикнули!',
  'play.buzzersClosed': 'Кнопки закрито — ведучий обирає.',
  'play.getReady': 'Приготуйтеся…',
  'play.waitingNext': 'Очікування наступного питання…',
  'play.calledOn': '📣 Ведучий викликав {name}',
  'play.correct': '✅ {name} відповів правильно (+{points})',
  'play.wrong': '❌ {name} помилився',
  'play.buzzedIn': 'Натиснули:',
  'play.scores': 'Рахунок',
  'play.gameOver': '🏁 Гру завершено!',
  'play.winner': '🥇 {name} перемагає з {score} балами',
  'play.leave': 'Вийти з гри',
  // «Норм чи Стрьом»
  'play.voteNorm': 'НОРМ',
  'play.voteCringe': 'СТРЬОМ',
  'play.voteNow': 'Голосуйте — можна передумати, поки не проголосували всі або не вийшов час.',
  'play.voteCast': 'Голос зараховано. Натисніть іншу кнопку, щоб змінити.',
  'play.voteClosed': 'Голосування закрито.',
  'play.voteResults': 'Зал вирішив…',
  'play.nobodyVoted': 'Ніхто не проголосував.',
  'play.voteSummary': 'Усі твердження — як бачив зал',
  'kind.label': 'Тип гри',
  'kind.quiz': '🔔 Квіз',
  'kind.normcringe': '👍👎 Норм чи Стрьом',
  'templates.voteLabels': 'Назви кнопок (необов’язково)',
  'templates.labelNorm': 'Ліва кнопка (сторона «норм»)',
  'templates.labelCringe': 'Права кнопка (сторона «стрьом»)',
  'templates.voteLabelsHelp': 'Порожньо — буде НОРМ / СТРЬОМ. Заповніть обидві, щоб перейменувати, напр. Так / Ні.',
  'templates.labelsBoth': 'Заповніть обидві назви кнопок або залиште обидві порожніми.',
  'templates.window': 'Вікно кнопки / голосування (секунд)',
  'templates.windowHelp': 'Порожньо = 30с. Скільки часу мають гравці після відкриття питання.',
  'templates.windowRange': 'Ціле число від 5 до 120.',
  'console.fastest': 'Бали (швидше = більше, до {max})',
  'console.hideAnswers': '🙈 Сховати відповіді',
  'console.showAnswers': '👁 Показати відповіді',
  'console.hideAnswersHelp': 'Ховає нотатку з відповіддю та правильну сторону до закриття раунду — коли цей екран на проєкторі.',
  'windowSeconds must be between 5 and 120': 'вікно має бути від 5 до 120 секунд',
  'play.donate': 'Донат',

  'login.title': 'Вхід для ведучого',
  'login.email': 'Ел. пошта',
  'login.password': 'Пароль',
  'login.newPassword': 'Новий пароль',
  'login.firstSignin': 'Перший вхід — оберіть новий пароль.',
  'login.signin': 'Увійти',
  'login.signingIn': 'Вхід…',
  'login.setPassword': 'Встановити пароль і продовжити',
  'login.failed': 'Помилка входу',

  'games.title': 'Панель ведучого',
  'games.templates': 'Шаблони',
  'games.authoring': 'Редактор',
  'games.history': 'Історія',
  'games.signout': 'Вийти',
  'games.newGame': 'Нова гра',
  'games.pickTopics': 'Торкніться тем у порядку, в якому хочете грати.',
  'games.noTopics': 'Ще немає тем.',
  'games.createTopics': 'Створити теми',
  'games.filterTopics': 'Фільтр тем…',
  'games.noMatch': 'Немає тем за вашим фільтром.',
  'games.createGame': 'Створити гру ({n})',
  'games.creating': 'Створення…',
  'games.donateLabel': 'Донат-лінк (необовʼязково)',
  'games.donatePlaceholder': 'https://…',
  'games.donateHelp': 'Показується гравцям на екрані входу та під час гри.',
  'games.recentGames': 'Останні ігри',
  'games.noGames': 'Ще немає ігор.',
  'games.openConsole': 'Відкрити консоль',
  'games.viewResults': 'Результати',
  'games.deleteConfirm': 'Видалити гру {code}? Це назавжди видалить її гравців, бали та результати.',

  'templates.title': 'Шаблони ігор',
  'templates.intro': 'Багаторазові набори тем. Налаштуйте один раз і запускайте ігри з нього.',
  'templates.new': 'Новий шаблон',
  'templates.yourTemplates': 'Ваші шаблони',
  'templates.none': 'Ще немає шаблонів. Створіть шаблон, щоб зберегти набір тем.',
  'templates.topicCount': 'Тем: {n}',
  'templates.startGame': 'Почати гру',
  'templates.deleteConfirm': 'Видалити шаблон «{name}»? Теми та ігри не постраждають.',
  'templates.newTitle': 'Новий шаблон',
  'templates.editTitle': 'Редагувати шаблон',
  'templates.namePlaceholder': 'Назва шаблону',
  'templates.lineup': 'Теми (перетягніть, щоб змінити порядок)',
  'templates.emptyLineup': 'Ще немає тем — додайте нижче.',
  'templates.addTopics': 'Додати теми',
  'templates.allAdded': 'Усі теми вже в цьому шаблоні.',
  'templates.nameRequired': 'Спочатку вкажіть назву шаблону.',
  'templates.noTopicsLeft': 'Теми цього шаблону більше не існують.',
  'templates.dragHandle': 'Перетягнути',

  'status.lobby': 'лобі',
  'status.active': 'активна',
  'status.finished': 'завершена',
  'qstate.idle': 'очікує',
  'qstate.open': 'відкрито',
  'qstate.closed': 'закрито',
  'res.correct': 'правильно',
  'res.incorrect': 'неправильно',

  'console.backGames': 'Ігри',
  'console.live': 'наживо',
  'console.connecting': 'підключення…',
  'console.loading': 'Завантаження консолі…',
  'console.join': 'Приєднання:',
  'console.copyLink': 'Копіювати',
  'console.copied': '✓ Скопійовано',
  'console.showQR': 'Показати QR',
  'console.hideQR': 'Сховати QR',
  'console.waitingPlayers': 'Очікування гравців…',
  'console.pressStart': 'Натисніть «Розпочати гру», коли всі приєднаються.',
  'console.startGame': 'Розпочати гру',
  'console.answer': 'Відповідь: {answer}',
  'console.noQuestion': 'Питання не вибрано.',
  'console.openBuzzer': 'Відкрити кнопку ({s}с)',
  'console.resetBuzzer': '🔄 Скинути кнопку',
  'console.endGame': 'Завершити гру',
  'console.endGameConfirm': 'Завершити гру?',
  'console.gameFinished': 'Гру завершено.',
  'console.buzzedIn': 'Натиснули ({n})',
  'console.noBuzzes': 'Ще ніхто не натиснув.',
  'console.callOn': 'Викликати',
  'console.correct': 'правильно (+{points})',
  'console.wrong': 'неправильно',
  'console.undo': 'Скасувати',
  'console.tooLate': 'Натиснув(ла) понад 5с після правильної відповіді — бали вже не нараховуються',
  'console.mode': 'Режим питання',
  'console.modeClassic': '🔔 Кнопка',
  'console.modeShout': '📣 Хто кричить',
  'console.shoutTitle': 'Хто кричить — нарахуйте відповідь',
  'console.shoutHint': 'Торкніться гравців, щоб обрати переможців (📣 = крикнув). Бали діляться порівну, з округленням угору, але не менше ніж ціна ÷ 10.',
  'console.selectShouted': 'Обрати всіх, хто крикнув ({n})',
  'console.award': 'Нарахувати по {points} балів ({n})',
  'console.scores': 'Рахунок',
  'console.noPlayers': 'Ще немає гравців.',
  'console.stateLabel': 'стан: {status} / {qstate}',
  'console.openVote': 'Відкрити голосування ({s}с)',
  'console.resetVote': '🔄 Скинути голосування',
  'console.votes': 'Голоси ({n})',
  'console.votesLive': 'Проголосували {n} з {total}. Результат покажеться, щойно проголосують усі або закінчиться таймер.',
  'console.votesIdle': 'Зачитайте твердження і відкрийте голосування.',
  'console.players': 'Гравці ({n})',

  'nav.prevTopic': 'Попередня тема',
  'nav.prevQuestion': 'Попереднє питання',
  'nav.nextQuestion': 'Наступне питання',
  'nav.nextTopic': 'Наступна тема',

  'qr.scan': 'Скануйте, щоб приєднатися',

  'authoring.title': 'Редактор',
  'authoring.bulkImport': 'Масовий імпорт',
  'authoring.importHelp': 'Завантажте JSON-файл (див. docs/import-template.json). Тема квізу потребує 5 питань; тема «Норм чи Стрьом» (kind: "normcringe", див. docs/norm-cringe-template.json) — будь-яку кількість тверджень.',
  'authoring.imported': 'Імпортовано {imported}, пропущено {skipped}.',
  'authoring.topics': 'Теми',
  'authoring.newTopicPlaceholder': 'Назва нової теми',
  'authoring.add': '＋ Додати',
  'authoring.filterTopics': 'Фільтр тем…',
  'authoring.newestFirst': 'Спочатку нові',
  'authoring.titleAZ': 'За назвою А–Я',
  'authoring.noTopics': 'Ще немає тем — додайте вище або імпортуйте JSON-файл.',
  'authoring.noMatch': 'Немає тем за вашим фільтром.',
  'authoring.deleteTopicConfirm': 'Видалити цю тему та її питання?',
  'authoring.deleteQuestionConfirm': 'Видалити це питання?',
  'authoring.qCount': '{n} П',
  'authoring.questionText': 'Текст питання',
  'authoring.answerHostOnly': 'Відповідь (лише ведучий)',
  'authoring.statementText': 'Твердження для голосування',
  'authoring.hostNote': 'Нотатка ведучого (необов’язково)',
  'authoring.attachMedia': '📎 Картинка / аудіо',
  'authoring.replaceMedia': 'Замінити',
  'authoring.uploading': 'Завантаження…',
  'authoring.uploadFailed': 'Не вдалося завантажити файл',
  'authoring.media.image': 'картинка',
  'authoring.media.audio': 'аудіо (лише ведучий)',
  'play.listenHost': 'Слухайте на екрані ведучого',
  'play.listenHostBoth': 'Обидва кліпи грають на екрані ведучого — потім обирайте',
  'authoring.sideMedia': 'Медіа для кожного варіанта',
  'play.correctAnswer': 'Правильна відповідь: {answer}',
  'play.youWereRight': '✅ Вгадали — +{points}',
  'play.youWereWrong': '❌ Цього разу ні',
  'authoring.correctSide': 'Правильна сторона (необов’язково)',
  'authoring.correctNone': 'Лише думка',
  'correct must be norm, cringe or empty': 'правильна відповідь має бути norm, cringe або порожня',
  'media uploads are not configured': 'завантаження файлів не налаштовано',
  'media too large (max 15 MB)': 'файл завеликий (макс. 15 МБ)',
  'mediaUrl must be an uploaded /media/ file or an https link': 'медіа має бути завантаженим файлом або https-посиланням',
  'mediaType must be image or audio': 'тип медіа має бути image або audio',
  'authoring.importFailed': 'Помилка імпорту',
  'authoring.failedLoad': 'Не вдалося завантажити',

  'lb.title': '🏆 Загальний рейтинг',
  'lb.loading': 'Завантаження…',
  'lb.noGames': 'Ще немає завершених ігор.',
  'lb.games': '{n} ігор',

  'history.title': 'Минулі ігри',
  'history.noFinished': 'Ще немає завершених ігор.',
  'history.noPlayers': 'Немає гравців.',

  'common.back': '← Ігри',
  'common.delete': 'Видалити',
  'common.edit': 'Редагувати',
  'common.save': 'Зберегти',
  'common.cancel': 'Скасувати',
  'common.add': 'Додати',

  // Error messages — keyed by the exact English text the server/client produces,
  // so t(errorMessage) localises them (and falls back to English otherwise).
  'join a game before buzzing': 'приєднайтеся до гри, перш ніж натискати',
  'the buzzer is closed': 'кнопку закрито',
  'too late — the window has closed': 'занадто пізно — вікно закрилося',
  'code and nickname are required': 'потрібні код і нікнейм',
  'game not found for that code': 'гру за цим кодом не знайдено',
  'this game has already finished': 'цю гру вже завершено',
  'admin action requires an admin connection attached to a game': 'потрібне підключення адміністратора до гри',
  'game not found': 'гру не знайдено',
  'game already started': 'гру вже розпочато',
  'start the game first': 'спершу розпочніть гру',
  'no current question to open': 'немає поточного питання для відкриття',
  'question is already open': 'питання вже відкрито',
  'reset the buzzer (or move on) before opening again': 'скиньте кнопку (або перейдіть далі), перш ніж відкривати знову',
  'warning: buzzer auto-close timer failed to start': 'попередження: не вдалося запустити таймер автозакриття',
  'no more questions in this topic — advance to the next topic': 'більше немає питань у цій темі — перейдіть до наступної',
  'already at the first question — go back a topic': 'це вже перше питання — поверніться до попередньої теми',
  'no more topics — end the game': 'більше немає тем — завершіть гру',
  'already on the first topic': 'це вже перша тема',
  'no current topic': 'немає поточної теми',
  'no current question to reset': 'немає поточного питання для скидання',
  'game has no topics': 'у грі немає тем',
  'current topic has no questions': 'у поточній темі немає питань',
  'invalid join payload': 'некоректні дані приєднання',
  'invalid callOn payload': 'некоректні дані виклику',
  'invalid mark payload': 'некоректні дані оцінки',
  'player not found': 'гравця не знайдено',
  'no active question': 'немає активного питання',
  'that player did not buzz for this question': 'цей гравець не натискав на це питання',
  'join a game before voting': 'приєднайтеся до гри, перш ніж голосувати',
  'voting is closed': 'голосування закрито',
  'this game has no voting': 'у цій грі немає голосування',
  'this game has no buzzer': 'у цій грі немає кнопки',
  'this game has no question modes': 'у цій грі немає режимів питань',
  'invalid vote payload': 'некоректні дані голосу',
  'unknown vote choice': 'невідомий варіант голосу',
  'this question has already been answered correctly': 'на це питання вже відповіли правильно',
  // REST
  'title is required': 'потрібна назва',
  'text and positive points are required': 'потрібні текст і додатні бали',
  'text is required': 'потрібен текст',
  'voteLabels only apply to normcringe lineups': 'назви кнопок доступні лише для «Норм чи Стрьом»',
  'voteLabels need both norm and cringe wording': 'потрібні обидві назви кнопок',
  'voteLabels must be at most 24 characters each': 'назва кнопки — не більше 24 символів',
  'topicOrder must contain at least one topic': 'потрібна принаймні одна тема',
  'payload must contain at least one topic': 'потрібна принаймні одна тема',
  'not found': 'не знайдено',
  'empty request body': 'порожній запит',
  // Client-side fallbacks
  'Not signed in': 'Ви не увійшли',
  'Failed to load': 'Не вдалося завантажити',
  'Failed to create game': 'Не вдалося створити гру',
  'Failed to delete game': 'Не вдалося видалити гру',
  'Failed to add topic': 'Не вдалося додати тему',
};

const dicts: Record<Lang, Dict> = { en, uk };

function detectLang(): Lang {
  const saved = localStorage.getItem('beernerd.lang');
  if (saved === 'en' || saved === 'uk') return saved;
  // Ukrainian is the default UI language; users can switch to EN and it sticks.
  return 'uk';
}

interface LangStore {
  lang: Lang;
  setLang: (l: Lang) => void;
}

export const useLang = create<LangStore>((set) => ({
  lang: detectLang(),
  setLang: (lang) => {
    localStorage.setItem('beernerd.lang', lang);
    set({ lang });
  },
}));

export type TFunc = (key: string, params?: Record<string, string | number>) => string;

export function useT(): TFunc {
  const lang = useLang((s) => s.lang);
  return (key, params) => {
    let str = dicts[lang][key] ?? en[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) str = str.replaceAll(`{${k}}`, String(v));
    }
    return str;
  };
}
